import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Plus, Database, Edit2, Trash2, Search, Filter, X, AlertTriangle, Loader2, ArrowLeft, History } from 'lucide-react';
import { useProjectStore } from '../../store';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import {
  useCreateModel,
  useUpdateModel,
  useDeleteModel,
} from '../../hooks/queries/useModelMutations';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../hooks/queries/queryKeys';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import ModelFormModal from './ModelFormModal';
import ModelDetailsModal from './ModelDetailsModal';
import { Agent, Model, CreateModelForm, CreateAgentForm, RegistryStatsResponse } from '../../types';
import ModelTable from './ModelTable';
import AgentDetailsModal from '../agents/AgentDetailsModal';
import AgentFormModal from '../agents/AgentFormModal';
import CustomSelect from '../common/CustomSelect';
import apiService from '../../services/api';
import ChangeList from '../history/ChangeList';
import { useVersionStore } from '../../store/versionStore';

interface ModelFilters {
  search: string;
  hasProperties: boolean | 'all';
  hasRelations: boolean | 'all';
}

interface SortConfig {
  field: 'class_name' | 'created_at' | 'updated_at';
  direction: 'asc' | 'desc';
}

const ModelsPage: React.FC = () => {
  // AC-1412 / AC-1377 P5.1: project models and agents flow through React
  // Query (`useModels` / `useAgents`); model CRUD flows through the
  // mutation hooks. Each mutation invalidates `projectModels` and
  // `projectRegistries` via `useModelMutations`, so the list refreshes
  // automatically without the manual loadModels() / loadAgents() reloads
  // the legacy store-driven path needed.
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.project_id;
  const modelsQuery = useModels(projectId);
  const agentsQuery = useAgents(projectId);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const queryClient = useQueryClient();
  const createModelMutation = useCreateModel(projectId ?? '');
  const updateModelMutation = useUpdateModel(projectId ?? '');
  const deleteModelMutation = useDeleteModel(projectId ?? '');
  const [searchParams, setSearchParams] = useSearchParams();
  const { getBackToWorkflow } = useContextualNavigation();
  const backToWorkflow = getBackToWorkflow(currentProject?.project_id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editModel, setEditModel] = useState<Model | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  // AC-1213: Agent details / edit modals opened from the "Used By Agents" pill.
  // Exactly one of viewedAgent / editingAgent is non-null at any time so the
  // user can flow View -> Edit -> Save without leaving the Schemas page.
  const [viewedAgent, setViewedAgent] = useState<Agent | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  const handleAgentFormSubmit = async (formData: CreateAgentForm) => {
    if (!editingAgent || !currentProject) return;
    try {
      await apiService.updateAgent(String(editingAgent.agent_id), formData);
      // AC-1412 / AC-1377 P5.1: refresh agents via the RQ cache instead
      // of the legacy loadAgents() store method. The active useAgents()
      // subscription will refetch on invalidation.
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectAgents(currentProject.project_id),
      });
      setEditingAgent(null);
    } catch (err) {
      console.error('Error updating agent:', err);
      setError('Failed to update agent');
    }
  };
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; model: Model | null }>({ open: false, model: null });
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
  const [initialRegistrySubTab, setInitialRegistrySubTab] = useState<'configuration' | 'entries' | undefined>(undefined);

  // Registry stats for models with registry_enabled=true (AC-385)
  const [registryStats, setRegistryStats] = useState<Record<number, number | null>>({});

  // Version history
  const {
    loadChanges,
    changes: allChanges,
    changesLoading,
    changesError,
  } = useVersionStore();
  const modelChanges = allChanges.filter(c => c.entity_type === 'model');

  // Filters and sorting
  const [filters, setFilters] = useState<ModelFilters>({
    search: '',
    hasProperties: 'all',
    hasRelations: 'all'
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (currentProject) {
      // AC-1412 / AC-1377 P5.1: useModels() and useAgents() subscribe
      // automatically; only the version-history side-fetch stays here.
      loadChanges(parseInt(String(currentProject.project_id), 10), { entity_type: 'model' });
    }
  }, [currentProject, loadChanges]);

  // AC-385: Fetch registry stats for models with registry_enabled=true
  const fetchRegistryStats = useCallback(async () => {
    const registryEnabledModels = models.filter(m => m.registry_enabled);
    if (registryEnabledModels.length === 0) {
      setRegistryStats({});
      return;
    }

    const statsPromises = registryEnabledModels.map(async (model) => {
      try {
        const stats = await apiService.getRegistryStats(model.model_id);
        return { modelId: model.model_id, count: stats.total_entries };
      } catch (err) {
        // On error, return null to indicate error state for fallback display
        console.error(`Failed to fetch registry stats for model ${model.model_id}:`, err);
        return { modelId: model.model_id, count: null };
      }
    });

    const results = await Promise.all(statsPromises);
    const statsMap: Record<number, number | null> = {};
    results.forEach(({ modelId, count }) => {
      statsMap[modelId] = count;
    });
    setRegistryStats(statsMap);
  }, [models]);

  useEffect(() => {
    fetchRegistryStats();
  }, [fetchRegistryStats]);

  // Handle query params to open a specific model's registry tab (from RegistriesPage)
  useEffect(() => {
    const modelIdParam = searchParams.get('modelId');
    const tabParam = searchParams.get('tab');
    const subtabParam = searchParams.get('subtab');

    if (modelIdParam && models.length > 0) {
      const modelId = parseInt(modelIdParam, 10);
      const model = models.find(m => m.model_id === modelId);
      if (model) {
        // Open the form modal in edit mode with the specified tab
        setEditModel(model);
        setModalOpen(true);
        if (tabParam === 'registry') {
          setInitialTab('registry');
          if (subtabParam === 'entries') {
            setInitialRegistrySubTab('entries');
          }
        }
        // Clear the query params after handling
        setSearchParams({});
      }
    }
  }, [models, searchParams, setSearchParams]);

  const handleOpenModal = () => {
    setEditModel(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditModel(null);
  };

  const handleView = (model: Model) => {
    setSelectedModel(model);
    setDetailsModalOpen(true);
  };

  // Filter and sort models
  const filteredAndSortedModels = useMemo(() => {
    let filtered = models.filter(model => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          model.class_name.toLowerCase().includes(searchLower) ||
          model.description?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Has properties filter
      if (filters.hasProperties !== 'all') {
        const hasProps = Object.keys(model.properties).length > 0;
        if (filters.hasProperties !== hasProps) return false;
      }

      // Has relations filter
      if (filters.hasRelations !== 'all') {
        const hasRels = Object.keys(model.relations || {}).length > 0;
        if (filters.hasRelations !== hasRels) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.field];
      let bValue: any = b[sortConfig.field];

      if (sortConfig.field === 'created_at' || sortConfig.field === 'updated_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [models, filters, sortConfig]);

  const handleCreate = async (data: CreateModelForm, editId?: number): Promise<void> => {
    if (!currentProject) return;
    setLoading(true);
    try {
      if (editId) {
        await updateModelMutation.mutateAsync({ modelId: editId, data });
      } else {
        await createModelMutation.mutateAsync({ data });
      }
      setModalOpen(false);
      setEditModel(null);
      // AC-1412 / AC-1377 P5.1: useUpdateModel / useCreateModel already
      // invalidate projectModels and projectRegistries on success — no
      // manual loadModels() refresh needed.
    } catch (err) {
      setError(editId ? 'Failed to update model' : 'Failed to create model');
      console.error('Error saving model:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (model: Model) => {
    setEditModel(model);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteDialog.model) {
      setLoading(true);
      try {
        await deleteModelMutation.mutateAsync({ modelId: deleteDialog.model.model_id });
        setDeleteDialog({ open: false, model: null });
        // AC-1412 / AC-1377 P5.1: useDeleteModel invalidates projectModels
        // and projectRegistries automatically; no manual reload needed.
      } catch (err: any) {
        // Handle specific error messages from the backend
        console.error('Error deleting model:', err);
        console.error('Error response:', err?.response);
        console.error('Error data:', err?.response?.data);
        
        if (err?.response?.data?.detail) {
          setError(err.response.data.detail);
        } else if (err?.message) {
          setError(err.message);
        } else {
          setError('Failed to delete schema. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSort = (field: SortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (!currentProject) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            No project selected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a project to manage models
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Back navigation */}
      <div className="mb-4">
        {backToWorkflow ? (
          <Link
            to={backToWorkflow.path}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {backToWorkflow.label}
          </Link>
        ) : currentProject && (
          <Link
            to={`/projects/${currentProject.project_id}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Overview
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Schemas</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Create and manage your schemas for {currentProject.name}
          </p>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          disabled={loading}
        >
          <Plus className="w-4 h-4" />
          New Schema
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-red-700 dark:text-red-400 whitespace-pre-line">{error}</div>
              {error.includes('agents') && (
                <div className="mt-3 text-sm text-red-600">
                  <p className="font-medium">To resolve this issue:</p>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Go to the <a href="/agents" className="text-primary-600 hover:text-primary-800 underline">Agents page</a> to edit the affected agents</li>
                    <li>For each agent, either change their output type to "string" or select a different output schema</li>
                    <li>Then try deleting this model again</li>
                  </ol>
                </div>
              )}
            </div>
            <button 
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search schemas..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border ${
              showFilters 
                ? 'bg-primary-50 text-blue-700 border-blue-200' 
                : 'bg-white dark:bg-cap-dark text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Has Properties</label>
                <CustomSelect
                  value={filters.hasProperties.toString()}
                  onChange={(value) => setFilters(prev => ({
                    ...prev,
                    hasProperties: value === 'all' ? 'all' : value === 'true'
                  }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'true', label: 'With Properties' },
                    { value: 'false', label: 'Without Properties' },
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Has Relations</label>
                <CustomSelect
                  value={filters.hasRelations.toString()}
                  onChange={(value) => setFilters(prev => ({
                    ...prev,
                    hasRelations: value === 'all' ? 'all' : value === 'true'
                  }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'true', label: 'With Relations' },
                    { value: 'false', label: 'Without Relations' },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading schemas...</span>
        </div>
      )}

      {/* Models List */}
      {!loading && (
        <>
          {filteredAndSortedModels.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
                {models.length === 0 ? 'No schemas yet' : 'No schemas match your filters'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {models.length === 0 
                  ? 'Get started by creating your first schema'
                  : 'Try adjusting your search or filters'}
              </p>
              {models.length === 0 && (
                <button
                  onClick={handleOpenModal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <Plus className="w-4 h-4" />
                  Create Schema
                </button>
              )}
            </div>
          ) : (
            <ModelTable
              models={filteredAndSortedModels}
              agents={agents}
              sortConfig={sortConfig}
              onSort={handleSort}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={(model: Model) => setDeleteDialog({ open: true, model })}
              onViewAgent={(agent: Agent) => setViewedAgent(agent)}
              registryStats={registryStats}
            />
          )}
        </>
      )}

      {/* Version History - collapsible */}
      {models.length > 0 && modelChanges.length > 0 && (
        <details className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <summary className="px-6 py-3 flex items-center gap-2 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800 list-none">
            <History className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Version History</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">({modelChanges.length})</span>
          </summary>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <ChangeList
              changes={modelChanges}
              projectId={currentProject?.project_id ? parseInt(String(currentProject.project_id), 10) : 0}
              loading={changesLoading}
              error={changesError}
              compact={true}
              onEntityChanged={() => {
                if (currentProject) {
                  // AC-1412 / AC-1377 P5.1: invalidate the RQ cache so
                  // useModels() refetches; the legacy loadModels()
                  // store method has been removed.
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.projectModels(currentProject.project_id),
                  });
                  loadChanges(parseInt(String(currentProject.project_id), 10), { entity_type: 'model' });
                }
              }}
            />
          </div>
        </details>
      )}

      {/* Create/Edit Modal */}
      <ModelFormModal
        open={modalOpen}
        onClose={() => {
          handleCloseModal();
          setInitialTab(undefined);
          setInitialRegistrySubTab(undefined);
        }}
        onSave={handleCreate}
        existingModels={models}
        editModel={editModel}
        initialTab={initialTab as 'properties' | 'registry' | undefined}
        initialRegistrySubTab={initialRegistrySubTab}
        onRegistryChange={fetchRegistryStats}
      />

      {/* Details Modal */}
      {detailsModalOpen && selectedModel && (
        <ModelDetailsModal
          model={selectedModel}
          allModels={models}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedModel(null);
          }}
          onEdit={() => {
            setDetailsModalOpen(false);
            handleEdit(selectedModel);
          }}
        />
      )}

      {/* Agent Details Modal - opened from "Used By Agents" pill (AC-1213) */}
      {viewedAgent && !editingAgent && (
        <AgentDetailsModal
          agent={viewedAgent}
          models={models}
          onClose={() => setViewedAgent(null)}
          onEdit={() => {
            // Swap details view for the edit form in place, keeping the
            // user on the Schemas page.
            setEditingAgent(viewedAgent);
            setViewedAgent(null);
          }}
        />
      )}

      {/* Agent Edit Form - reached from the details modal's Edit button (AC-1213) */}
      {editingAgent && (
        <AgentFormModal
          agent={editingAgent}
          models={models}
          availableAgents={agents}
          onSubmit={handleAgentFormSubmit}
          onClose={() => setEditingAgent(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && deleteDialog.model && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">Delete Schema</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the schema "{deleteDialog.model.class_name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteDialog({ open: false, model: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelsPage;